import { useState, useEffect, useCallback } from 'react';
import { Account } from '../types';
import { api, AccountData } from '../services/api';

function parseAccount(data: AccountData): Account {
  return {
    ...data,
    starting_balance: Number(data.starting_balance),
  };
}

const LAST_ACCOUNT_KEY = 'balancescript_last_account_id';

export function useAccounts() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const selectAccount = useCallback((id: number) => {
    setSelectedAccountId(id);
    localStorage.setItem(LAST_ACCOUNT_KEY, String(id));
  }, []);

  const fetchAccounts = useCallback(async () => {
    try {
      const data = await api.getAccounts();
      const parsed = data.map(parseAccount);
      setAccounts(parsed);
      if (parsed.length > 0 && !selectedAccountId) {
        const savedId = localStorage.getItem(LAST_ACCOUNT_KEY);
        const savedAccount = savedId ? parsed.find(a => a.id === Number(savedId)) : null;
        selectAccount(savedAccount ? savedAccount.id : parsed[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId, selectAccount]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const selectedAccount = accounts.find(a => a.id === selectedAccountId) || null;

  const createAccount = async (name: string, startingBalance?: number) => {
    const data = await api.createAccount({ name, starting_balance: startingBalance });
    const parsed = parseAccount(data);
    setAccounts(prev => [...prev, parsed]);
    selectAccount(parsed.id);
    return parsed;
  };

  const updateAccount = async (id: number, updates: Partial<{ name: string; starting_balance: number; private: boolean }>) => {
    const data = await api.updateAccount(id, updates);
    const parsed = parseAccount(data);
    setAccounts(prev => prev.map(a => a.id === id ? parsed : a));
    return parsed;
  };

  return {
    accounts,
    selectedAccount,
    selectedAccountId,
    setSelectedAccountId: selectAccount,
    loading,
    createAccount,
    updateAccount,
  };
}
